import moment from "moment-timezone";

// Market open/closed by asset class, evaluated in US Eastern time.
// MUST mirror the backend schedule (app/Helper/Helper.php) that governs order
// execution, so the indicator matches when orders actually fill:
//
//   crypto → 24/7, always open.                         (Helper: crypto ⇒ always open)
//   forex  → opens Sunday 5:00pm ET, closes Friday 5:00pm ET, closed Saturday.
//            (Helper::checkForexMarketHours)
//   stocks → Mon-Fri, 8:00 AM to 8:00 PM ET — hour-based: open when the ET hour
//            is >= 8 and < 20. (Helper::checkMarketHours — NOT the 9:30 NYSE bell,
//            and no longer the 5:00 PM close either: client, "let it run until
//            8 p.m. ET and keep stock trading open until then; if the price is
//            moving live and accurately, why not?" The trading window and the
//            competition window are now the same 8-to-8. See the backend's
//            STOCK_TRADING_END_HOUR in config/constants.php.)
//
// NOTE: market holidays are NOT modeled here (the backend doesn't either).
export function isMarketOpen(category) {
  const cat = String(category || "").toLowerCase();
  if (cat === "crypto") return true;

  const now = moment().tz("America/New_York");
  const day = now.day(); // 0 = Sun … 6 = Sat
  const minutes = now.hours() * 60 + now.minutes();

  if (cat === "forex") {
    // Continuous Sun 17:00 ET → Fri 17:00 ET.
    if (day === 6) return false; // Saturday — closed all day
    if (day === 0) return minutes >= 17 * 60; // Sunday — opens 5:00pm
    if (day === 5) return minutes < 17 * 60; // Friday — closes 5:00pm
    return true; // Mon–Thu — 24h
  }

  // Stocks / equities / ETFs — mirror Helper::checkMarketHours (hour-based).
  if (day === 0 || day === 6) return false; // weekend
  const hour = now.hours();
  return hour >= 8 && hour < 20; // 8:00 AM–7:59 PM ET (closes at 8:00 PM)
}

// { open, label } — label is the exact copy the UI shows.
export function marketStatus(category) {
  const open = isMarketOpen(category);
  return { open, label: open ? "Market open" : "Market closed" };
}

// U.S. stock session for the Home Markets strip — client: "Show the stock session
// status: Pre-Market, Market Open, After Hours or Market Closed, reflecting our
// 8 AM to 8 PM ET coverage."
//
//   8:00 AM – 9:30 AM ET  Pre-Market
//   9:30 AM – 4:00 PM ET  Market Open
//   4:00 PM – 8:00 PM ET  After Hours
//   otherwise, weekends and holidays  Market Closed
//
// `calendar` comes from the market-strip endpoint:
//   holidays     — "YYYY-MM-DD" full-day NYSE closures → Closed all day
//   early_closes — { "YYYY-MM-DD": "HH:mm" } regular-session close in ET.
// Client: "Switch to After Hours at 1 PM ET on early-close days. Follow the actual
// holiday schedule for when the extended session ends rather than assuming 8 PM."
// On those days the exchanges' post-market session runs four hours past the early
// close (1:00 → 5:00 PM ET), so After hours ends at close + 4h.
// Client (second round): "Status values: Pre-market, Open, After hours, Closed."
export const STOCK_SESSIONS = {
  pre: "Pre-market",
  open: "Open",
  after: "After hours",
  closed: "Closed",
};

const EARLY_CLOSE_POST_MARKET_MINUTES = 4 * 60;

export function stockSession(calendar = {}, now = moment()) {
  const et = moment(now).tz("America/New_York");
  const day = et.day();
  const date = et.format("YYYY-MM-DD");
  const minutes = et.hours() * 60 + et.minutes();
  if (day === 0 || day === 6 || (calendar.holidays || []).includes(date)) {
    return "closed";
  }
  const early = (calendar.early_closes || {})[date];
  const [h, m] = (early || "16:00").split(":").map(Number);
  const close = h * 60 + m;
  const extendedEnd = early ? close + EARLY_CLOSE_POST_MARKET_MINUTES : 20 * 60;
  if (minutes >= 8 * 60 && minutes < 9 * 60 + 30) return "pre";
  if (minutes >= 9 * 60 + 30 && minutes < close) return "open";
  if (minutes >= close && minutes < extendedEnd) return "after";
  return "closed";
}
